import { Component } from '@theme/component';

/**
 * Team avatar row (sections/sl-team.liquid).
 *
 * The markup is one native scroll container in every mode, so this component never builds or
 * rebuilds slides. It only does the three things CSS cannot:
 *
 * 1. Decides between `grid` and `carousel` by measuring whether the grid still fits. Liquid sets
 *    the initial mode from the block count; `data-locked="true"` means that decision was "more
 *    cards than the grid allows" and must never be undone by a measurement.
 * 2. Keeps the arrows' disabled state in sync with the scroll position.
 * 3. Drops the track's tab stop when there is nothing left to scroll, and publishes the scroll
 *    ratio and the visible fraction as custom properties for the mobile
 *    progress bar. Both have CSS defaults matching the Figma resting state, so a script failure
 *    leaves a correct-looking bar rather than an empty one.
 *
 * Nothing here is required for the section to be usable: without JavaScript the track still
 * scrolls, and below 750px the stylesheet pins the mobile carousel on its own.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling flex row.
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class TeamComponent extends Component {
  /** @type {string[]} */
  requiredRefs = ['track'];

  /** Matches the `gap` floor of `.sl-team__track` in assets/sl-team.css. */
  static GRID_GAP = 8;

  /**
   * The `behavior` option beats the stylesheet's `scroll-behavior`, so reduced motion has to be
   * re-checked here rather than left to the CSS guard.
   *
   * @returns {ScrollBehavior}
   */
  static scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#wire();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** The Section Rendering API replaces the subtree — re-observe the new track and re-measure. */
  updatedCallback() {
    this.#teardown();
    this.#wire();
  }

  #wire() {
    const { track } = this.refs;
    if (!track) return;

    // Fresh controller per connect: a component that is disconnected and re-connected must not
    // attach its listeners to an already-aborted signal.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }

    if (typeof ResizeObserver === 'function') {
      // The observer's first callback runs right after the next layout, before paint, and does the
      // first measurement. Measuring here as well read clientWidth/scrollWidth synchronously and
      // forced a layout while the page was still evaluating its modules (Lighthouse forced reflow).
      this.#resizeObserver = new ResizeObserver(() => {
        this.#syncMode();
        this.#syncScroll();
      });
      this.#resizeObserver.observe(track);
    } else {
      this.#syncMode();
      this.#syncScroll();
    }
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** Scroll one page forward. */
  next() {
    this.#page(1);
  }

  /** Scroll one page back. */
  previous() {
    this.#page(-1);
  }

  /**
   * Advance by as many whole cards as currently fit, so a five-up carousel does not need five
   * clicks to reveal the next screen. Always at least one card.
   *
   * @param {number} direction 1 forward, -1 back.
   */
  #page(direction) {
    const { track } = this.refs;
    if (!track) return;

    const step = this.#cardStep();
    if (!step) return;

    const perPage = Math.max(1, Math.floor(track.clientWidth / step));
    track.scrollBy({ left: direction * perPage * step, behavior: TeamComponent.scrollBehavior() });
  }

  /** Card width plus the gap between two cards, in pixels. */
  #cardStep() {
    const { track } = this.refs;
    if (!track) return 0;

    const styles = getComputedStyle(track);
    const card = parseFloat(styles.getPropertyValue('--sl-team-card'));
    const gap = parseFloat(styles.columnGap);

    if (!Number.isFinite(card) || card <= 0) return 0;

    return card + (Number.isFinite(gap) ? gap : 0);
  }

  /**
   * Narrowest the grid can be: every card at its *minimum* width, separated by the CSS gap floor.
   *
   * The floor is the measure that matters, not the design width — grid cards shrink (see
   * `.sl-team__card` in the stylesheet), so a container a little narrower than 1440 still shows
   * every member rather than collapsing into a carousel. Only when even the floor overflows is the
   * grid genuinely out of room.
   *
   * Measuring it instead of reading `scrollWidth` is what lets the component answer "would the
   * grid fit again?" while the track is currently laid out as a carousel.
   */
  #gridWidth() {
    const { track } = this.refs;
    if (!track) return 0;

    const count = track.children.length;
    if (!count) return 0;

    const styles = getComputedStyle(this);
    const min = parseFloat(styles.getPropertyValue('--sl-team-card-min'));
    const card = parseFloat(styles.getPropertyValue('--sl-team-card-grid'));
    const width = Number.isFinite(min) && min > 0 ? min : card;
    if (!Number.isFinite(width) || width <= 0) return 0;

    return count * width + (count - 1) * TeamComponent.GRID_GAP;
  }

  /**
   * Width of the section's content column, in either mode.
   *
   * `track.clientWidth` on its own cannot answer this: while bleeding, the track spans the whole
   * section, so it reports a different number per mode and the mode decision feeds back into
   * itself — grid says "does not fit, switch to carousel", carousel then measures the wider
   * bleeding track and says "it fits, switch back", forever.
   *
   * Its *content box* is stable, though, because the inline padding that makes the bleed is
   * exactly the gutter on each side: in grid mode the padding is 0 and the client width already is
   * the column, and in carousel mode client width minus the two gutters is that same column.
   */
  #contentWidth() {
    const { track } = this.refs;
    if (!track) return 0;

    const styles = getComputedStyle(track);
    const padding = parseFloat(styles.paddingInlineStart) + parseFloat(styles.paddingInlineEnd);

    return track.clientWidth - (Number.isFinite(padding) ? padding : 0);
  }

  #syncMode() {
    const { track } = this.refs;
    if (!track) return;

    // Locked by the block count — the grid is not an option however wide the viewport gets.
    if (this.dataset.locked === 'true') {
      this.dataset.mode = 'carousel';
      return;
    }

    // Below 750px the stylesheet owns the presentation; leave the server-rendered value alone so
    // nothing changes underfoot when the viewport crosses back over the breakpoint.
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const required = this.#gridWidth();
    const available = this.#contentWidth();
    if (!required || !available) return;

    // 1px of slack: sub-pixel widths must not read as "does not fit".
    this.dataset.mode = required > available + 1 ? 'carousel' : 'grid';
  }

  #onScroll = () => {
    this.#syncScroll();
  };

  #syncScroll() {
    const { track, previous, next } = this.refs;
    if (!track) return;

    const overflow = track.scrollWidth - track.clientWidth;
    // `scrollLeft` runs negative in a right-to-left document.
    const position = Math.abs(track.scrollLeft);
    const ratio = overflow > 0 ? Math.min(1, position / overflow) : 0;
    const visible = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;

    this.style.setProperty('--sl-team-progress', String(ratio));
    this.style.setProperty('--sl-team-progress-size', `${(visible * 100).toFixed(3)}%`);

    if (previous instanceof HTMLButtonElement) previous.disabled = position <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = overflow <= 0 || position >= overflow - 1;

    // A scrollable region has to be reachable by keyboard, so the markup ships `tabindex="0"` and
    // it stays correct with no script. Once there is nothing to scroll — the grid fits — that tab
    // stop leads nowhere, so drop it rather than making keyboard users pass through it.
    track.tabIndex = overflow > 0 ? 0 : -1;
  }

  /**
   * Bring the card the merchant just selected in the theme editor into view.
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const card = target.closest('.sl-team__card');
    if (!(card instanceof HTMLElement)) return;

    card.scrollIntoView({ behavior: TeamComponent.scrollBehavior(), block: 'nearest', inline: 'center' });
  };
}

if (!customElements.get('sl-team-component')) {
  customElements.define('sl-team-component', TeamComponent);
}
